import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';

/**
 * JobCompletionModal - Modal for completing a job with review and blacklist options
 * 
 * Props:
 * - show: boolean - whether modal is visible
 * - onHide: function - called when modal is closed
 * - onSubmit: function - called with completion data { starRating, reviewText, isBlacklisted, blacklistReason }
 * - job: object - the job being completed (for customer info)
 * - isSubmitting: boolean - whether submission is in progress
 */
export default function JobCompletionModal({ show, onHide, onSubmit, job, isSubmitting = false }) {
  const [starRating, setStarRating] = useState(5); // Default 5 stars
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [isBlacklisted, setIsBlacklisted] = useState(false);
  const [blacklistReason, setBlacklistReason] = useState('');
  const [completionReason, setCompletionReason] = useState('');
  const [sendSMSReview, setSendSMSReview] = useState(false);
  const [tipAmount, setTipAmount] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate completion reason if rating is less than 5 stars
    if (starRating < 5 && !completionReason.trim()) {
      alert('Please provide a reason for the low rating');
      return;
    }
    
    // Validate blacklist reason if blacklist is checked
    if (isBlacklisted && !blacklistReason.trim()) {
      alert('Please provide a reason for blacklisting this customer');
      return;
    }
    
    onSubmit({
      starRating,
      reviewText: reviewText.trim(),
      isBlacklisted,
      blacklistReason: isBlacklisted ? blacklistReason.trim() : '',
      completionReason: starRating < 5 ? completionReason.trim() : '',
      tipAmount: tipAmount.trim(),
      sendSMSReview
    });
  };

  const handleClose = () => {
    // Reset form
    setStarRating(5);
    setReviewText('');
    setIsBlacklisted(false);
    setBlacklistReason('');
    setCompletionReason('');
    setSendSMSReview(false);
    setTipAmount('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton style={{ backgroundColor: '#28a745', color: 'white' }}>
        <Modal.Title>✅ Complete Job</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {job && (
          <div className="mb-3 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
            <small>
              <strong>{job.customerName}</strong> • {job.customerPhone}<br/>
              {job.from} → {job.to}
            </small>
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Star Rating Section */}
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <strong>⭐</strong>
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  size={32}
                  style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                  color={(hoverRating || starRating) >= star ? '#ffc107' : '#e4e5e9'}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setStarRating(star)}
                />
              ))}
              <span className="ms-1" style={{ fontWeight: 'bold' }}>
                {starRating}/5
              </span>
            </div>
          </div>

          {/* Optional Review Text */}
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={2}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Notes (optional)"
              maxLength={500}
              size="sm"
            />
          </Form.Group>

          {/* Tips Input */}
          <Form.Group className="mb-3">
            <Form.Label className="mb-1">Tips (£)</Form.Label>
            <Form.Control
              type="text"
              value={tipAmount}
              onChange={(e) => setTipAmount(e.target.value)}
              placeholder="e.g. £2"
              size="sm"
            />
          </Form.Group>

          {/* Completion Reason Section - Show when rating is less than 5 */}
          {starRating < 5 && (
            <div className="mb-3 p-2" style={{ 
              backgroundColor: '#fff3cd', 
              borderRadius: '6px',
              border: '2px solid #ffc107'
            }}>
              <Form.Label className="mb-2">
                <span style={{ fontWeight: 'bold' }}>⚠️ Reason for Low Rating *</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={completionReason}
                onChange={(e) => setCompletionReason(e.target.value)}
                placeholder="Please explain why the rating is less than 5 stars..."
                maxLength={1000}
                required={starRating < 5}
                size="sm"
                style={{ borderColor: starRating < 5 && !completionReason ? '#dc3545' : undefined }}
              />
            </div>
          )}

          {/* Blacklist Section */}
          <div className="mb-2 p-2" style={{ 
            backgroundColor: isBlacklisted ? '#fff3cd' : '#f8f9fa', 
            borderRadius: '6px',
            border: isBlacklisted ? '2px solid #ffc107' : '1px solid #dee2e6'
          }}>
            <Form.Check
              type="checkbox"
              id="blacklistCheckbox"
              checked={isBlacklisted}
              onChange={(e) => setIsBlacklisted(e.target.checked)}
              label={<span style={{ fontWeight: 'bold' }}>⚠️ Blacklist</span>}
            />

            {isBlacklisted && (
              <Form.Control
                className="mt-2"
                as="textarea"
                rows={2}
                value={blacklistReason}
                onChange={(e) => setBlacklistReason(e.target.value)}
                placeholder="Reason *"
                maxLength={1000}
                required={isBlacklisted}
                size="sm"
                style={{ borderColor: isBlacklisted && !blacklistReason ? '#dc3545' : undefined }}
              />
            )}
          </div>

          {/* SMS Review Link Option */}
          <div className="mb-2 p-2" style={{ 
            backgroundColor: '#e7f3ff', 
            borderRadius: '6px',
            border: '1px solid #007bff'
          }}>
            <Form.Check
              type="checkbox"
              id="sendSMSReviewCheckbox"
              checked={sendSMSReview}
              onChange={(e) => setSendSMSReview(e.target.checked)}
              label={<span style={{ fontWeight: 'bold' }}>📱 Send SMS Review Link</span>}
            />
            <small className="text-muted d-block mt-1">
              Customer will receive SMS with link to review
            </small>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="py-2">
        <Button 
          variant="secondary" 
          size="sm"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          variant="success" 
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting || (isBlacklisted && !blacklistReason.trim()) || (starRating < 5 && !completionReason.trim())}
        >
          {isSubmitting ? (
            <><span className="spinner-border spinner-border-sm me-1" />Completing...</>
          ) : (
            <>✅ Complete</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

